import React, { useState, useEffect } from 'react';
import { X, Key, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../services/api';
import { GlassButton } from './ui/GlassButton';

interface GatewaySettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function GatewaySettingsModal({ isOpen, onClose }: GatewaySettingsModalProps) {
    const [activeGateway, setActiveGateway] = useState<'podpay' | 'slimpay'>('podpay');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadSettings();
        }
    }, [isOpen]);

    const loadSettings = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get('/gateway-settings');
            const d = response.data || {};
            setActiveGateway((d.active_gateway === 'slimpay' ? 'slimpay' : 'podpay'));
        } catch (err: any) {
            console.error('Error loading settings:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess(false);
        try {
            await api.post('/gateway-settings', { active_gateway: activeGateway });
            setSuccess(true);
            setTimeout(() => onClose(), 1500);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erro ao salvar configurações.');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="gateway-settings-modal" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(8px)'
        }}>
            <div className="gateway-settings-modal-inner" style={{
                background: '#0a0a0c',
                borderRadius: '28px',
                padding: '32px',
                width: '95%',
                maxWidth: '600px',
                border: '1px solid #222',
                boxShadow: '0 50px 100px rgba(0, 0, 0, 0.9)',
                maxHeight: '90vh',
                overflowY: 'auto'
            }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '14px',
                            background: 'rgba(255, 106, 0, 0.1)',
                            border: '1px solid rgba(255, 106, 0, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Key size={22} color="#ff6a00" />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: 'white' }}>Gateways de Pagamento</h2>
                            <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Configure como você quer receber suas vendas.</p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background: '#1a1a1c', border: 'none', borderRadius: '12px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifySelf: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <X size={18} color="#666" />
                    </button>
                </div>

                {/* Gateway ativo */}
                <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontSize: '11px', fontWeight: 800, color: '#888', textTransform: 'uppercase' }}>
                        Gateway ativo para vendas
                    </label>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#ccc' }}>
                            <input
                                type="radio"
                                name="activeGateway"
                                checked={activeGateway === 'podpay'}
                                onChange={() => setActiveGateway('podpay')}
                                style={{ accentColor: '#ff6a00' }}
                            />
                            <span>PodPay</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#ccc' }}>
                            <input
                                type="radio"
                                name="activeGateway"
                                checked={activeGateway === 'slimpay'}
                                onChange={() => setActiveGateway('slimpay')}
                                style={{ accentColor: '#ff6a00' }}
                            />
                            <span>Slimpay</span>
                        </label>
                    </div>
                </div>

                {/* Info: chaves do .env */}
                <div style={{ marginBottom: '24px', padding: '14px', background: 'rgba(255,106,0,0.06)', border: '1px solid rgba(255,106,0,0.15)', borderRadius: '12px', color: '#aaa', fontSize: '13px' }}>
                    <CheckCircle size={16} style={{ verticalAlign: 'middle', marginRight: '8px', color: '#ff6a00' }} />
                    As chaves são lidas do arquivo <code style={{ background: '#111', padding: '2px 6px', borderRadius: '4px' }}>.env</code> no servidor. Use o seletor acima para escolher qual gateway gera o PIX.
                </div>

                {/* Webhooks */}
                <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: 'rgba(255,106,0,0.05)', borderRadius: '10px', border: '1px solid rgba(255,106,0,0.1)' }}>
                        <AlertCircle size={14} color="#ff6a00" />
                        <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>Webhook PodPay: <code style={{ color: '#ff6a00' }}>https://seu-dominio.com/api/webhooks/podpay</code></p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: 'rgba(167,139,250,0.05)', borderRadius: '10px', border: '1px solid rgba(167,139,250,0.1)' }}>
                        <AlertCircle size={14} color="#a78bfa" />
                        <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>Webhook Slimpay: <code style={{ color: '#a78bfa' }}>https://seu-dominio.com/api/webhooks/slimpay</code></p>
                    </div>
                </div>

                {error && <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(255,0,0,0.05)', border: '1px solid rgba(255,0,0,0.2)', borderRadius: '12px', color: '#ff4d4d', fontSize: '13px' }}>{error}</div>}
                {success && <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(0,255,0,0.05)', border: '1px solid rgba(0,255,0,0.2)', borderRadius: '12px', color: '#22c55e', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircle size={16} /> Configurações salvas!</div>}

                <GlassButton
                    onClick={handleSave}
                    disabled={saving}
                    style={{ width: '100%', height: '52px', background: 'var(--btn-gradient)', color: '#000', fontSize: '15px', fontWeight: 800 }}
                >
                    {saving ? 'Processando...' : 'Salvar Alterações'}
                </GlassButton>
            </div>
        </div>
    );
}
